import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  Users,
  Building2,
  Receipt,
  Package,
  CreditCard,
  FileText,
  AlertTriangle,
  Stethoscope,
  GraduationCap,
  BookOpen,
  TrendingUp,
  X,
  Eye,
  Plus,
  Edit2,
  Trash2,
  UserPlus,
  UserMinus
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardAdmin = () => {
  const { isSindacatoAdmin, hasPermesso } = useAuth();
  const [data, setData] = useState(null);
  const [scontrini, setScontrini] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [ruoli, setRuoli] = useState([]);
  const [cittadini, setCittadini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAzienda, setSelectedAzienda] = useState(null);
  const [aziendaDialogOpen, setAziendaDialogOpen] = useState(false);
  
  // Edit states
  const [scontrinoDialogOpen, setScontrinoDialogOpen] = useState(false);
  const [editingScontrino, setEditingScontrino] = useState(null);
  const [scontrinoForm, setScontrinoForm] = useState({
    descrizione: '',
    importo: 0,
    tipo: 'ENTRATA',
    azienda_id: '',
    data_operazione: new Date().toISOString().split('T')[0]
  });

  // Dipendente dialog
  const [dipendenteDialogOpen, setDipendenteDialogOpen] = useState(false);
  const [dipendenteForm, setDipendenteForm] = useState({
    user_id: '',
    ruolo_azienda_id: ''
  });

  const canModifyScontrini = () => isSindacatoAdmin() || hasPermesso('modifica_scontrini_admin');
  const canModifyDipendenti = () => isSindacatoAdmin() || hasPermesso('modifica_dipendenti_admin');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, scontriniRes, ruoliRes, cittadiniRes] = await Promise.all([
        axios.get(`${API_URL}/admin/dashboard`),
        axios.get(`${API_URL}/admin/scontrini`),
        axios.get(`${API_URL}/ruoli-azienda`),
        axios.get(`${API_URL}/all-users-complete`)
      ]);
      setData(dashRes.data);
      setScontrini(scontriniRes.data);
      setRuoli(ruoliRes.data);
      setCittadini(cittadiniRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const fetchDipendentiAzienda = async (aziendaId) => {
    try {
      const res = await axios.get(`${API_URL}/users?azienda_id=${aziendaId}`);
      setDipendenti(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getRuoliPerAzienda = (aziendaId) => {
    return ruoli.filter(r => r.azienda_id === aziendaId);
  };

  const getCittadiniNonInAzienda = (aziendaId) => {
    // Mostra TUTTI i cittadini, non solo quelli non assunti
    return cittadini.filter(c => c.ruolo !== 'SINDACATO_ADMIN');
  };
  
  // Verifica se un utente è già dipendente di un'azienda specifica
  const isGiaDipendenteAzienda = (userId, aziendaId) => {
    return dipendenti.some(d => d.id === userId);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const openAziendaDetail = async (azienda) => {
    setSelectedAzienda(azienda);
    await fetchDipendentiAzienda(azienda.id);
    setAziendaDialogOpen(true);
  };

  const getScontriniPerAzienda = (aziendaId) => {
    return scontrini.filter(s => s.azienda_id === aziendaId);
  };

  const openNewScontrino = (aziendaId = '') => {
    setEditingScontrino(null);
    setScontrinoForm({
      descrizione: '',
      importo: 0,
      tipo: 'ENTRATA',
      azienda_id: aziendaId,
      data_operazione: new Date().toISOString().split('T')[0]
    });
    setScontrinoDialogOpen(true);
  };

  const openAddDipendente = () => {
    setDipendenteForm({
      user_id: '',
      ruolo_azienda_id: ''
    });
    setDipendenteDialogOpen(true);
  };

  const handleAddDipendente = async () => {
    if (!dipendenteForm.user_id || !dipendenteForm.ruolo_azienda_id) {
      toast.error('Seleziona utente e ruolo');
      return;
    }
    try {
      await axios.post(`${API_URL}/assunzioni?user_id=${dipendenteForm.user_id}&azienda_id=${selectedAzienda.id}&ruolo_azienda_id=${dipendenteForm.ruolo_azienda_id}`);
      toast.success('Dipendente aggiunto');
      setDipendenteDialogOpen(false);
      await fetchDipendentiAzienda(selectedAzienda.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleRemoveDipendente = async (userId) => {
    if (!window.confirm('Rimuovere questo dipendente dall\'azienda?')) return;
    try {
      await axios.post(`${API_URL}/assunzioni/licenzia/${userId}/${selectedAzienda.id}`, {
        motivo: 'Rimosso dalla Dashboard Admin'
      });
      toast.success('Dipendente rimosso');
      await fetchDipendentiAzienda(selectedAzienda.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleSaveScontrino = async () => {
    if (!scontrinoForm.descrizione || !scontrinoForm.importo || !scontrinoForm.azienda_id) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    try {
      if (editingScontrino) {
        await axios.patch(`${API_URL}/scontrini/${editingScontrino.id}`, {
          descrizione: scontrinoForm.descrizione,
          importo: parseFloat(scontrinoForm.importo),
          tipo: scontrinoForm.tipo,
          data_operazione: scontrinoForm.data_operazione
        });
        toast.success('Scontrino aggiornato');
      } else {
        await axios.post(`${API_URL}/scontrini`, {
          azienda_id: scontrinoForm.azienda_id,
          descrizione: scontrinoForm.descrizione,
          importo: parseFloat(scontrinoForm.importo),
          tipo: scontrinoForm.tipo,
          data_operazione: scontrinoForm.data_operazione
        });
        toast.success('Scontrino creato');
      }
      setScontrinoDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteScontrino = async (id) => {
    if (!window.confirm('Eliminare questo scontrino?')) return;
    try {
      await axios.delete(`${API_URL}/scontrini/${id}`);
      toast.success('Scontrino eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (!isSindacatoAdmin() && !hasPermesso('visualizza_dashboard_admin')) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Accesso riservato</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="space-y-6" data-testid="dashboard-admin-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
          <LayoutDashboard className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard Admin</h1>
          <p className="text-slate-500">Panoramica completa di tutti i gestionali</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-3xl font-bold text-blue-900">{stats.utenti || 0}</p>
                <p className="text-sm text-blue-600">Utenti</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-3xl font-bold text-amber-900">{stats.aziende || 0}</p>
                <p className="text-sm text-amber-600">Aziende</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Receipt className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-3xl font-bold text-green-900">{stats.scontrini || 0}</p>
                <p className="text-sm text-green-600">Scontrini</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 col-span-2 lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-900">{formatCurrency(stats.fatturato_totale || 0)}</p>
                <p className="text-sm text-emerald-600">Fatturato Totale</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-3xl font-bold text-purple-900">{stats.prodotti || 0}</p>
                <p className="text-sm text-purple-600">Prodotti</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-cyan-600" />
              <div>
                <p className="text-3xl font-bold text-cyan-900">{stats.conti_bancari || 0}</p>
                <p className="text-sm text-cyan-600">Conti Bancari</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-rose-600" />
              <div>
                <p className="text-3xl font-bold text-rose-900">{stats.curriculum || 0}</p>
                <p className="text-sm text-rose-600">Curriculum</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-3xl font-bold text-red-900">{stats.multe || 0}</p>
                <p className="text-sm text-red-600">Multe</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Stethoscope className="w-8 h-8 text-teal-600" />
              <div>
                <p className="text-3xl font-bold text-teal-900">{stats.cartelle_cliniche || 0}</p>
                <p className="text-sm text-teal-600">Cartelle Cliniche</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-indigo-600" />
              <div>
                <p className="text-3xl font-bold text-indigo-900">{stats.lezioni || 0}</p>
                <p className="text-sm text-indigo-600">Lezioni</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-3xl font-bold text-orange-900">{stats.voti || 0}</p>
                <p className="text-sm text-orange-600">Voti</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="aziende">
        <TabsList>
          <TabsTrigger value="aziende" className="gap-2">
            <Building2 className="w-4 h-4" />
            Aziende
          </TabsTrigger>
          <TabsTrigger value="scontrini" className="gap-2">
            <Receipt className="w-4 h-4" />
            Tutti gli Scontrini
          </TabsTrigger>
        </TabsList>

        {/* Aziende */}
        <TabsContent value="aziende" className="mt-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {data?.aziende?.map((az) => (
              <Card 
                key={az.id} 
                className="hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-purple-300"
                onClick={() => openAziendaDetail(az)}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {az.nome}
                        <Eye className="w-4 h-4 text-slate-400" />
                      </CardTitle>
                      <CardDescription>{az.tipo}</CardDescription>
                    </div>
                    <Badge variant="outline">{az.dipendenti} dipendenti</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg">
                      <span className="text-sm text-slate-600">Scontrini</span>
                      <Badge>{az.scontrini?.count || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg">
                      <span className="text-sm text-slate-600">Fatturato</span>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(az.scontrini?.totale || 0)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Scontrini */}
        <TabsContent value="scontrini" className="mt-6">
          {canModifyScontrini() && (
            <div className="flex justify-end mb-4">
              <Button onClick={() => openNewScontrino()} className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Scontrino
              </Button>
            </div>
          )}
          {scontrini.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Receipt className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">Nessuno scontrino registrato</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {scontrini.slice(0, 50).map((s) => (
                <Card key={s.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-slate-900">
                          {s.quantita}x {s.prodotto_nome}
                        </p>
                        <p className="text-sm text-slate-500">
                          <span className="text-blue-600">{s.acquirente_nome}</span>
                          {' → '}
                          <span className="text-amber-600">{s.venditore_azienda_nome}</span>
                        </p>
                        <p className="text-xs text-slate-400">
                          {new Date(s.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className="text-xl font-bold text-green-600">
                            {formatCurrency(s.totale)}
                          </span>
                          <p className="text-xs text-slate-400">
                            {formatCurrency(s.prezzo_unitario)} cad.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {scontrini.length > 50 && (
                <p className="text-center text-slate-500 text-sm">
                  Mostrati i primi 50 di {scontrini.length} scontrini
                </p>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Dettaglio Azienda */}
      <Dialog open={aziendaDialogOpen} onOpenChange={setAziendaDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedAzienda?.nome}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAzienda && (
            <div className="space-y-6 mt-4">
              {/* Info Generali */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-slate-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{selectedAzienda.dipendenti}</p>
                    <p className="text-sm text-slate-600">Dipendenti</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{selectedAzienda.scontrini?.count || 0}</p>
                    <p className="text-sm text-slate-600">Scontrini</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-xl font-bold text-green-600">{formatCurrency(selectedAzienda.scontrini?.totale || 0)}</p>
                    <p className="text-sm text-slate-600">Fatturato</p>
                  </CardContent>
                </Card>
              </div>

              {/* Info Azienda */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Informazioni</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Tipo:</span>
                      <span className="font-medium">{selectedAzienda.tipo}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">ID:</span>
                      <span className="font-mono text-xs">{selectedAzienda.id}</span>
                    </div>
                    {selectedAzienda.descrizione && (
                      <div className="pt-2">
                        <span className="text-slate-600">Descrizione:</span>
                        <p className="mt-1">{selectedAzienda.descrizione}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Scontrini Azienda */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      Ultimi Scontrini
                    </CardTitle>
                    {canModifyScontrini() && (
                      <Button size="sm" variant="outline" onClick={() => openNewScontrino(selectedAzienda.id)}>
                        <Plus className="w-3 h-3 mr-1" />
                        Aggiungi
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {getScontriniPerAzienda(selectedAzienda.id).length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Nessuno scontrino</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {getScontriniPerAzienda(selectedAzienda.id).slice(0, 20).map((s) => (
                        <div key={s.id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <div>
                            <p className="text-sm font-medium">{s.descrizione}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(s.data_operazione || s.created_at).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={s.tipo === 'ENTRATA' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {s.tipo === 'ENTRATA' ? '+' : '-'}{formatCurrency(s.importo)}
                            </Badge>
                            {canModifyScontrini() && (
                              <Button size="sm" variant="ghost" className="text-red-600 h-6 w-6 p-0" onClick={() => handleDeleteScontrino(s.id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Dipendenti Azienda */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Dipendenti ({dipendenti.length})
                    </CardTitle>
                    {canModifyDipendenti() && (
                      <Button size="sm" variant="outline" onClick={openAddDipendente}>
                        <UserPlus className="w-3 h-3 mr-1" />
                        Aggiungi
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {dipendenti.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Nessun dipendente</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {dipendenti.map((d) => (
                        <div key={d.id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <div>
                            <p className="text-sm font-medium">{d.nome} {d.cognome}</p>
                            <p className="text-xs text-slate-500">@{d.username} • {d.ruolo_azienda_nome || 'Nessun ruolo'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{d.ruolo}</Badge>
                            {canModifyDipendenti() && d.ruolo !== 'SINDACATO_ADMIN' && (
                              <Button size="sm" variant="ghost" className="text-red-600 h-6 w-6 p-0" onClick={() => handleRemoveDipendente(d.id)}>
                                <UserMinus className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setAziendaDialogOpen(false)}>
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Nuovo/Modifica Scontrino */}
      <Dialog open={scontrinoDialogOpen} onOpenChange={setScontrinoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingScontrino ? 'Modifica Scontrino' : 'Nuovo Scontrino'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Azienda</Label>
              <Select value={scontrinoForm.azienda_id} onValueChange={(v) => setScontrinoForm({...scontrinoForm, azienda_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona azienda..." />
                </SelectTrigger>
                <SelectContent>
                  {data?.aziende?.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo Operazione</Label>
              <Select value={scontrinoForm.tipo} onValueChange={(v) => setScontrinoForm({...scontrinoForm, tipo: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ENTRATA">Entrata</SelectItem>
                  <SelectItem value="USCITA">Uscita</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Importo (€)</Label>
              <Input 
                type="number"
                step="0.01"
                min="0"
                value={scontrinoForm.importo}
                onChange={(e) => setScontrinoForm({...scontrinoForm, importo: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input 
                value={scontrinoForm.descrizione}
                onChange={(e) => setScontrinoForm({...scontrinoForm, descrizione: e.target.value})}
                placeholder="Descrizione dell'operazione..."
              />
            </div>
            <div className="space-y-2">
              <Label>Data Operazione</Label>
              <Input 
                type="date"
                value={scontrinoForm.data_operazione}
                onChange={(e) => setScontrinoForm({...scontrinoForm, data_operazione: e.target.value})}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setScontrinoDialogOpen(false)}>Annulla</Button>
              <Button onClick={handleSaveScontrino}>Salva</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Aggiungi Dipendente */}
      <Dialog open={dipendenteDialogOpen} onOpenChange={setDipendenteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Aggiungi Dipendente a {selectedAzienda?.nome}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Seleziona Cittadino</Label>
              <Select value={dipendenteForm.user_id} onValueChange={(v) => setDipendenteForm({...dipendenteForm, user_id: v})}>
                <SelectTrigger data-testid="dipendente-user-select">
                  <SelectValue placeholder="Cerca cittadino..." />
                </SelectTrigger>
                <SelectContent className="max-h-64 overflow-y-auto">
                  {getCittadiniNonInAzienda(selectedAzienda?.id).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nome} {c.cognome} (@{c.username})
                    </SelectItem>
                  ))}
                  {getCittadiniNonInAzienda(selectedAzienda?.id).length === 0 && (
                    <div className="px-2 py-3 text-sm text-slate-500 text-center">
                      {cittadini.length === 0 ? 'Caricamento cittadini...' : 'Nessun cittadino disponibile'}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ruolo</Label>
              <Select value={dipendenteForm.ruolo_azienda_id} onValueChange={(v) => setDipendenteForm({...dipendenteForm, ruolo_azienda_id: v})}>
                <SelectTrigger data-testid="dipendente-ruolo-select">
                  <SelectValue placeholder="Seleziona ruolo..." />
                </SelectTrigger>
                <SelectContent className="max-h-64 overflow-y-auto">
                  {getRuoliPerAzienda(selectedAzienda?.id).map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDipendenteDialogOpen(false)}>Annulla</Button>
              <Button onClick={handleAddDipendente} className="bg-blue-600 hover:bg-blue-700">
                <UserPlus className="w-4 h-4 mr-2" />
                Aggiungi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardAdmin;
